/**
 * Export to Cloud Storage - React Query Hooks
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'
import {
  getConnections,
  disconnectProvider,
  startExport,
  startExportAllGuestUploads,
  getExportJob,
  cancelExportJob,
} from './api'
import type {
  CloudConnection,
  ExportProvider,
  GetConnectionsResponse,
} from '@/types/export'

export const exportKeys = {
  all: ['export'] as const,
  connections: () => [...exportKeys.all, 'connections'] as const,
  job: (jobId: string) => [...exportKeys.all, 'job', jobId] as const,
}

// OAuth connect: optimistic cache for instant UI; delayed invalidate avoids refetch overwriting with a still-empty GET
const CONNECTIONS_RECONCILE_DELAY_MS = 750

export function applyOptimisticCloudConnection(
  queryClient: QueryClient,
  provider: ExportProvider,
  email?: string
): void {
  const newConnection: CloudConnection = {
    provider,
    email: email ?? '',
    connectedAt: new Date().toISOString(),
  }
  queryClient.setQueryData<GetConnectionsResponse>(
    exportKeys.connections(),
    (old) => {
      const list = old?.data?.connections ?? []
      return {
        success: true,
        data: {
          connections: [
            ...list.filter((c) => c.provider !== provider),
            newConnection,
          ],
        },
      }
    }
  )
}

export function scheduleCloudConnectionsReconcile(
  queryClient: QueryClient
): void {
  if (typeof window === 'undefined') return
  window.setTimeout(() => {
    void queryClient.invalidateQueries({ queryKey: exportKeys.connections() })
  }, CONNECTIONS_RECONCILE_DELAY_MS)
}

/**
 * Get user's connected cloud accounts
 */
export function useCloudConnections(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: exportKeys.connections(),
    queryFn: getConnections,
    enabled: options?.enabled !== false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Disconnect a cloud provider
 */
export function useDisconnectProvider() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (provider: ExportProvider) => disconnectProvider(provider),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: exportKeys.connections() })
    },
  })
}

/**
 * Start an export job for a specific invitation
 */
export function useStartExport() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      invitationId,
      folderName,
    }: {
      invitationId: string
      folderName?: string
    }) => startExport(invitationId, folderName ? { folderName } : undefined),
    onSuccess: (data) => {
      // Prefetch the job status
      queryClient.prefetchQuery({
        queryKey: exportKeys.job(data.data.jobId),
        queryFn: () => getExportJob(data.data.jobId),
      })
    },
  })
}

/**
 * Start an export job for ALL user's guest uploads
 */
export function useStartExportAllGuestUploads() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (options?: { folderName?: string }) =>
      startExportAllGuestUploads(options),
    onSuccess: (data) => {
      // Prefetch the job status
      queryClient.prefetchQuery({
        queryKey: exportKeys.job(data.data.jobId),
        queryFn: () => getExportJob(data.data.jobId),
      })
    },
  })
}

/**
 * Get export job status (with polling option)
 */
export function useExportJob(
  jobId: string | null,
  options?: { enabled?: boolean; pollInterval?: number }
) {
  return useQuery({
    queryKey: exportKeys.job(jobId || ''),
    queryFn: () => getExportJob(jobId!),
    enabled: !!jobId && options?.enabled !== false,
    refetchInterval: (query) => {
      // Stop polling if job is completed or failed
      const status = query.state.data?.data.status
      if (status === 'completed' || status === 'failed') {
        return false
      }
      return options?.pollInterval ?? 2000 // Poll every 2 seconds by default
    },
  })
}

/**
 * Cancel an export job
 */
export function useCancelExportJob() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (jobId: string) => cancelExportJob(jobId),
    onSuccess: (_, jobId) => {
      queryClient.invalidateQueries({ queryKey: exportKeys.job(jobId) })
    },
  })
}
